import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Globe, Save, Loader2, Edit, Trash2, Plus, Check, X, ChevronDown, ChevronUp,
  Users, Briefcase, Clock, Flag, MapPin
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CountriesManagement = ({ token }) => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCountry, setEditingCountry] = useState(null);
  const [expandedCountry, setExpandedCountry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newRegion, setNewRegion] = useState({ id: '', name: '' });

  const emptyCountry = {
    code: '',
    name: '',
    flag: '🏳️',
    currency: 'EUR',
    available: false,
    coming_soon: true,
    regions_label: 'Región',
    regions: [],
    order: 99
  };

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      const res = await axios.get(`${API}/countries/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCountries(res.data.countries || []);
    } catch (error) {
      toast.error('Error cargando países');
    } finally {
      setLoading(false);
    }
  };

  const saveCountry = async () => {
    if (!editingCountry?.code || !editingCountry?.name) {
      toast.error('Código y nombre son obligatorios');
      return;
    }

    setSaving(true);
    try {
      const isNew = !countries.find(c => c.code === editingCountry.code);
      
      if (isNew) {
        await axios.post(`${API}/countries/admin/create`, editingCountry, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('País creado');
      } else {
        await axios.put(`${API}/countries/admin/${editingCountry.code}`, editingCountry, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('País actualizado');
      }
      
      setEditingCountry(null);
      loadCountries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando país');
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailability = async (country) => {
    try {
      const endpoint = country.available 
        ? `${API}/countries/admin/${country.code}/disable`
        : `${API}/countries/admin/${country.code}/enable`;
      
      await axios.put(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(country.available ? 'País deshabilitado' : 'País habilitado');
      loadCountries();
    } catch (error) {
      toast.error('Error cambiando estado');
    }
  };

  const deleteCountry = async (code) => {
    if (!window.confirm('¿Eliminar este país? Esta acción no se puede deshacer.')) return;
    
    try {
      await axios.delete(`${API}/countries/admin/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('País eliminado');
      loadCountries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando país');
    }
  };

  const addRegion = () => {
    if (!newRegion.id || !newRegion.name) {
      toast.error('Completa ID y nombre de la región');
      return;
    }
    
    setEditingCountry(prev => ({
      ...prev,
      regions: [...(prev.regions || []), { ...newRegion }]
    }));
    setNewRegion({ id: '', name: '' });
  };

  const removeRegion = (regionId) => {
    setEditingCountry(prev => ({
      ...prev,
      regions: prev.regions.filter(r => r.id !== regionId)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Gestión de Países</h3>
          <p className="text-sm text-slate-500">Configura los países disponibles en la plataforma</p>
        </div>
        <Button onClick={() => setEditingCountry({ ...emptyCountry })} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> Nuevo País
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Globe className="w-4 h-4 text-emerald-600" />
            <span className="text-sm text-emerald-700">Países Activos</span>
          </div>
          <p className="text-2xl font-bold text-emerald-800">
            {countries.filter(c => c.available).length}
          </p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-700">Próximamente</span>
          </div>
          <p className="text-2xl font-bold text-amber-800">
            {countries.filter(c => c.coming_soon && !c.available).length}
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-700">Usuarios Totales</span>
          </div>
          <p className="text-2xl font-bold text-blue-800">
            {countries.reduce((sum, c) => sum + (c.users_count || 0), 0)}
          </p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="w-4 h-4 text-purple-600" />
            <span className="text-sm text-purple-700">Profesiones Totales</span>
          </div>
          <p className="text-2xl font-bold text-purple-800">
            {countries.reduce((sum, c) => sum + (c.professions_count || 0), 0)}
          </p>
        </div>
      </div>

      {/* Countries List */}
      <div className="space-y-3">
        {countries.map(country => (
          <div key={country.code} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Country Header */}
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
              onClick={() => setExpandedCountry(expandedCountry === country.code ? null : country.code)}
            >
              <div className="flex items-center gap-4">
                <span className="text-3xl">{country.flag}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-slate-800">{country.name}</h4>
                    <span className="text-xs text-slate-400 font-mono">{country.code}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <span>{country.regions?.length || 0} regiones</span>
                    <span>•</span>
                    <span>{country.professions_count || 0} profesiones</span>
                    <span>•</span>
                    <span>{country.users_count || 0} usuarios</span>
                    {country.waitlist_count > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-amber-600">{country.waitlist_count} en lista de espera</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {country.available ? (
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                    Disponible
                  </span>
                ) : country.coming_soon ? (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Próximamente
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                    Inactivo
                  </span>
                )}
                
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); toggleAvailability(country); }}
                    title={country.available ? 'Deshabilitar' : 'Habilitar'}
                  >
                    {country.available ? <X className="w-4 h-4 text-rose-500" /> : <Check className="w-4 h-4 text-emerald-500" />}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); setEditingCountry(country); }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); deleteCountry(country.code); }}
                  >
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </div>
                
                {expandedCountry === country.code ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>
            </div>

            {/* Expanded Details */}
            {expandedCountry === country.code && (
              <div className="p-4 bg-slate-50 border-t border-slate-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-slate-500">Moneda</p>
                    <p className="font-medium">{country.currency}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Etiqueta Regiones</p>
                    <p className="font-medium">{country.regions_label}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Orden</p>
                    <p className="font-medium">{country.order}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Lista de Espera</p>
                    <p className="font-medium">{country.waitlist_count || 0} usuarios</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Regiones ({country.regions?.length || 0})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {country.regions?.map(region => (
                      <span 
                        key={region.id} 
                        className="px-2 py-1 bg-white border border-slate-200 rounded text-xs text-slate-600"
                      >
                        {region.name}
                      </span>
                    ))}
                    {(!country.regions || country.regions.length === 0) && (
                      <span className="text-xs text-slate-400">Sin regiones configuradas</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingCountry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold">
                {countries.find(c => c.code === editingCountry.code) ? 'Editar País' : 'Nuevo País'}
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm text-slate-600">Código ISO*</label>
                  <Input
                    value={editingCountry.code}
                    onChange={(e) => setEditingCountry({ ...editingCountry, code: e.target.value.toUpperCase() })}
                    disabled={countries.find(c => c.code === editingCountry.code)}
                    placeholder="ES"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Bandera (emoji)</label>
                  <Input
                    value={editingCountry.flag}
                    onChange={(e) => setEditingCountry({ ...editingCountry, flag: e.target.value })}
                    placeholder="🇪🇸"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-slate-600">Nombre*</label>
                  <Input
                    value={editingCountry.name}
                    onChange={(e) => setEditingCountry({ ...editingCountry, name: e.target.value })}
                    placeholder="España"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-slate-600">Moneda</label>
                  <Input
                    value={editingCountry.currency}
                    onChange={(e) => setEditingCountry({ ...editingCountry, currency: e.target.value.toUpperCase() })}
                    placeholder="EUR"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Etiqueta Regiones</label>
                  <Input
                    value={editingCountry.regions_label}
                    onChange={(e) => setEditingCountry({ ...editingCountry, regions_label: e.target.value })}
                    placeholder="Comunidad Autónoma"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Orden</label>
                  <Input
                    type="number"
                    value={editingCountry.order}
                    onChange={(e) => setEditingCountry({ ...editingCountry, order: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingCountry.available}
                    onChange={(e) => setEditingCountry({ ...editingCountry, available: e.target.checked, coming_soon: !e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Disponible</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingCountry.coming_soon}
                    onChange={(e) => setEditingCountry({ ...editingCountry, coming_soon: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Mostrar Próximamente</span>
                </label>
              </div>

              {/* Regions */}
              <div>
                <label className="text-sm text-slate-600 mb-2 block">Regiones</label>
                <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                  {editingCountry.regions?.map((region, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <span className="text-xs text-slate-400 w-20 font-mono">{region.id}</span>
                      <span className="flex-1 text-sm">{region.name}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeRegion(region.id)}>
                        <Trash2 className="w-3 h-3 text-rose-500" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <Input
                    placeholder="ID (ej: madrid)"
                    value={newRegion.id}
                    onChange={(e) => setNewRegion({ ...newRegion, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                    className="w-32"
                  />
                  <Input
                    placeholder="Nombre (ej: Madrid)"
                    value={newRegion.name}
                    onChange={(e) => setNewRegion({ ...newRegion, name: e.target.value })}
                    className="flex-1"
                  />
                  <Button variant="outline" onClick={addRegion}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditingCountry(null)}>Cancelar</Button>
              <Button onClick={saveCountry} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountriesManagement;
